import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'

interface AuthContextType {
  isAuthenticated: boolean
  login: () => void
  logout: () => void
  checkAuth: () => boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  // Check if user is authenticated on component mount
  useEffect(() => {
    checkAuth()
  }, [])

  const checkAuth = (): boolean => {
    try {
      const authStatus = localStorage.getItem('waterplan_authenticated')
      const authTimestamp = localStorage.getItem('waterplan_auth_timestamp')
      
      if (authStatus === 'true' && authTimestamp) {
        const timestamp = parseInt(authTimestamp)
        const now = Date.now()
        const sessionDuration = 24 * 60 * 60 * 1000 // 24 hours in milliseconds
        
        // Check if session is still valid (within 24 hours)
        if (now - timestamp < sessionDuration) {
          setIsAuthenticated(true)
          return true
        } else {
          // Session expired, clear storage
          localStorage.removeItem('waterplan_authenticated')
          localStorage.removeItem('waterplan_auth_timestamp')
          setIsAuthenticated(false)
          return false
        }
      }
      
      setIsAuthenticated(false)
      return false
    } catch (error) {
      console.error('Error checking authentication:', error)
      setIsAuthenticated(false)
      return false
    }
  }

  const login = () => {
    setIsAuthenticated(true)
  }

  const logout = () => {
    localStorage.removeItem('waterplan_authenticated')
    localStorage.removeItem('waterplan_auth_timestamp')
    setIsAuthenticated(false)
  }

  const value: AuthContextType = {
    isAuthenticated,
    login,
    logout,
    checkAuth
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
